import axios from 'axios';

// Use environment variable for API URL
const API_URL = process.env.REACT_APP_API_URL;

if (!API_URL) {
  console.error('⚠️ تحذير: لم يتم تعيين REACT_APP_API_URL في ملف .env');
  console.error('⚠️ يرجى إضافة REACT_APP_API_URL=http://your-server-url/api في ملف client/.env');
}

// Simple in-memory cache to reduce redundant requests
const cache: Record<string, { data: any; timestamp: number }> = {};
const CACHE_DURATION = 10 * 1000; // 10 seconds cache

// Request throttling to prevent too many concurrent requests
const MAX_CONCURRENT_REQUESTS = 5;
let activeRequests = 0;
const requestQueue: (() => void)[] = [];

// Function to process the queue
const processQueue = () => {
  if (activeRequests < MAX_CONCURRENT_REQUESTS && requestQueue.length > 0) {
    const request = requestQueue.shift();
    if (request) {
      request();
    }
  }
};

// Create axios instance with custom configuration
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 180000, // 3 minute timeout for large exports
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Simple retry mechanism for failed requests
const retryRequest = async (config: any, retryCount = 0, maxRetries = 3) => {
  try {
    return await axiosInstance(config);
  } catch (error: any) {
    // Only retry on network errors or 5xx (server) errors
    if (
      retryCount < maxRetries &&
      (
        !error.response ||
        (error.response && error.response.status >= 500) ||
        error.code === 'ECONNABORTED'
      )
    ) {
      // Exponential backoff delay
      const delay = Math.pow(2, retryCount) * 1000;
      console.log(`Retrying request (${retryCount + 1}/${maxRetries}) after ${delay}ms`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryRequest(config, retryCount + 1, maxRetries);
    }
    
    throw error;
  }
};

// Add authentication interceptor
axiosInstance.interceptors.request.use(
  config => {
    // Add authentication token from localStorage
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['x-auth-token'] = token;
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log('رمز المصادقة تم إضافته للطلب:', config.url);
    } else {
      console.warn('رمز المصادقة غير موجود عند محاولة الوصول إلى:', config.url);
      
      // محاولة تجديد الجلسة إذا كان رمز المصادقة غير موجود
      if (config.url &&
          !config.url.includes('/auth/login') &&
          !config.url.includes('/auth/me')) {
        console.log('جاري محاولة إعادة تحميل الصفحة للحصول على رمز مصادقة جديد...');
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Request interceptor for throttling and caching
axiosInstance.interceptors.request.use(
  async (config) => {
    // Only attempt to use cache for GET requests
    if (config.method?.toLowerCase() === 'get' && config.url) {
      const cacheKey = `${config.method}:${config.url}${config.params ? JSON.stringify(config.params) : ''}`;
      const cachedData = cache[cacheKey];
      
      // Return cached data if it exists and is still valid
      if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
        // Set a flag to cancel the actual request
        config.adapter = async () => {
          return {
            data: cachedData.data,
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
            request: {}
          };
        };
        return config;
      }
    }
    
    // Add timestamp parameter to certain GET requests to prevent browser caching
    if (config.method?.toLowerCase() === 'get' && config.url) {
      // Don't add timestamp to all URLs - only those that need to be fresh
      if (
        config.url.includes('/products/') ||
        config.url.includes('/inventory/') ||
        config.url.includes('/warehouse-transfers/')
      ) {
        config.params = {
          ...config.params,
          _: new Date().getTime()
        };
      }
    }

    // For non-cached requests, implement throttling
    return new Promise((resolve) => {
      const executeRequest = () => {
        activeRequests++;
        resolve(config);
      };

      if (activeRequests >= MAX_CONCURRENT_REQUESTS) {
        // Queue the request if too many active requests
        requestQueue.push(executeRequest);
      } else {
        executeRequest();
      }
    });
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and request count management
axiosInstance.interceptors.response.use(
  (response) => {
    // Decrement active requests count and process queue
    activeRequests = Math.max(0, activeRequests - 1);
    processQueue();
    
    // Store successful GET responses in cache
    if (response.config.method?.toLowerCase() === 'get' && response.config.url) {
      const cacheKey = `${response.config.method}:${response.config.url}${response.config.params ? JSON.stringify(response.config.params) : ''}`;
      
      // Don't cache specific paths
      const shouldNotCache = [
        '/auth/',
        '/users/'
      ].some(path => response.config.url?.includes(path));
      
      if (!shouldNotCache) {
        cache[cacheKey] = {
          data: response.data,
          timestamp: Date.now()
        };
      }
    }
    
    return response;
  },
  async (error) => {
    // Decrement active requests count and process queue
    activeRequests = Math.max(0, activeRequests - 1);
    processQueue();

    // Enhanced error logging
    if (error.response) {
      // Server responded with error status
      console.error(`API Error ${error.response.status}:`, {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: error.config?.url,
        method: error.config?.method
      });
      
      // Handle specific error responses
      switch (error.response.status) {
        case 400: // Bad Request - قد يكون خطأ في الباركود المكرر
          if (error.response.data?.type === 'barcode_duplicate') {
            console.warn('خطأ في الباركود المكرر:', error.response.data.error);
            // لا نحتاج لمعالجة خاصة هنا، سيتم التعامل معه في المكون
          } else if (error.response.data?.details && Array.isArray(error.response.data.details)) {
            console.warn('أخطاء متعددة في الباركود:', error.response.data.details);
          }
          break;
          
        case 401: // Unauthorized
          console.warn('خطأ مصادقة: المستخدم غير مصادق أو الرمز المميز غير صالح');
          
          // التحقق من أننا لسنا بالفعل في صفحة تسجيل الدخول لتجنب حلقات إعادة التوجيه
          const currentPath = window.location.pathname;
          
          // نقوم بتحديث رسالة الخطأ حسب نوع المسار
          let authErrorMessage = '';
          if (error.config.url.includes('/products')) {
            authErrorMessage = 'فشل الوصول إلى بيانات المنتجات. يرجى إعادة تسجيل الدخول.';
          } else if (error.config.url.includes('/categories')) {
            authErrorMessage = 'فشل الوصول إلى بيانات التصنيفات. يرجى إعادة تسجيل الدخول.';
          } else {
            authErrorMessage = 'انتهت جلسة العمل. يرجى تسجيل الدخول مرة أخرى للمتابعة.';
          }
          
          if (!currentPath.includes('/login')) {
            // مسح الرمز المميز غير الصالح
            localStorage.removeItem('token');
            localStorage.removeItem('activeBranchId');
            
            // استخدام نافذة تنبيه واحدة فقط لتجنب الإزعاج
            if (!(window as any).isShowingAuthAlert) {
              (window as any).isShowingAuthAlert = true;
              alert(authErrorMessage);
              
              // إعادة التوجيه إلى صفحة تسجيل الدخول
              window.location.href = '/login';
              
              // منع المزيد من المعالجة
              return new Promise(() => {});
            }
          }
          break;
          
        case 429: // Too Many Requests
          console.warn('Rate limit exceeded. The system is receiving too many requests.');
          // Show user notification here
          break;
          
        case 503: // Service Unavailable
          console.error('Server is currently unavailable or under maintenance.');
          // Show maintenance notification here
          break;
      }
    } else if (error.request) {
      // Request was made but no response received (server down or network issue)
      console.error('No response received from server:', {
        url: error.config?.url,
        method: error.config?.method,
        message: error.message
      });
    } else {
      // Error in setting up the request
      console.error('Error setting up request:', error.message);
    }

    return Promise.reject(error);
  }
);

// Utility function to create a request with automatic retry
export const requestWithRetry = (config: any, maxRetries = 3) => {
  return retryRequest(config, 0, maxRetries);
};

// Helper function to clear the cache
export const clearCache = (pattern?: RegExp) => {
  if (pattern) {
    // Clear only matching cache entries
    Object.keys(cache).forEach(key => {
      if (pattern.test(key)) {
        delete cache[key];
      }
    });
  } else {
    // Clear all cache
    Object.keys(cache).forEach(key => delete cache[key]);
  }
};

// Function to get current cache statistics
export const getCacheStats = () => {
  return {
    totalEntries: Object.keys(cache).length,
    oldestEntry: Object.entries(cache).reduce((oldest, [key, entry]) => {
      return (!oldest || entry.timestamp < oldest.timestamp) ? entry : oldest;
    }, null as null | { data: any; timestamp: number }),
    totalSizeKB: JSON.stringify(cache).length / 1024
  };
};

export default axiosInstance;